import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Play, Pause } from 'lucide-react';
import { Link } from 'react-router-dom';
import Modal from '../shared/Modal';
import PodcastContactForm from '../forms/PodcastContactForm';

interface Episode {
  title: string;
  path: string;
  description?: string;
}

export default function InTheLabCaseStudy() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [audioRefs, setAudioRefs] = useState<{ [key: string]: HTMLAudioElement | null }>({});

  const openContactModal = () => setIsContactModalOpen(true);
  const closeContactModal = () => setIsContactModalOpen(false);

  const episodes: Episode[] = [
    { title: "Untapping the Superconscious", path: "/assets/Inthelab/Untapping Superconsciousness.wav" },
    { title: "Unveiling the Wisdom of The Kybalion", path: "/assets/Inthelab/Episode 2_ Unveiling the Wisdom of The Kybalion.wav" },
    { title: "Exploring '1,000 Things Worth Knowing'", path: "/assets/Inthelab/Episode 3_ Exploring _1,000 Things Worth Knowing.wav" },
    { title: "Unlocking True Memory Power", path: "/assets/Inthelab/Episode 4_ Unlocking True Memory Power.wav" },
    { title: "The Intersection of Spiritual Power and Remote Sensing", path: "/assets/Inthelab/Episode 5_ The Intersection of Spiritual Power and Remote Sensing.wav" },
    { title: "Dreams, The Human Aura, & Unconscious Memory", path: "/assets/Inthelab/Episode 6_ Dreams, The Human Aura, & Unconscious Memory.wav" },
    { title: "A Deep Dive into the Subconscious Mind", path: "/assets/Inthelab/Episode 7_ A Deep Dive into the Subconscious Mind.wav" },
    { title: "Quantum Physics", path: "/assets/Inthelab/Episode 8_ Quantum Physics_.wav" },
    { title: "A Deep Dive Into Nikola Tesla's Mind", path: "/assets/Inthelab/Episode_ A Deep Dive Into Nikola Tesla's Mind.wav" },
    { title: "Who is Thoth?", path: "/assets/Inthelab/Episode 10_ Who is Thoth_.wav" },
    { title: "Universal Base Income With AuRA", path: "/assets/Inthelab/Episode 11_ Universal Base Income With AuRA.wav" },
    { title: "Inside the Mind of Milan Cheeks", path: "/assets/Inthelab/Episode 12_ Inside the Mind of Milan Cheeks—The Visionary Behind AuRA and SELF Labs Inc_.wav" },
    { title: "Cultivating the Future", path: "/assets/Inthelab/Episode 13_ Cultivating the Future—The Groundbreaking Partnership of SELF Labs Inc_, Box4Grow, and Beyond Imagination.wav" },
    { title: "Introducing Cerebral OS", path: "/assets/Inthelab/Episode 14_ Introducing Cerebral OS—Milan Cheeks of SELF Labs Inc_'s Non-Invasive BCI Software.wav" },
    { title: "Exploring the Future of Organoid Technology", path: "/assets/Inthelab/Episode 15_ Exploring the Future of Organoid Technology—SELF Labs Inc_'s Vision for AI and Quantum Computing.wav" },
    { title: "Ensuring Clean Water", path: "/assets/Inthelab/Episode 16_ Ensuring Clean Water—The Partnership Between SELF Labs Inc_ and Smart Choice Water.wav" },
    { title: "Pioneering the Future of Commerce", path: "/assets/Inthelab/Episode 17_ Pioneering the Future of Commerce—SELF Labs Inc_ Transforms Ecommerce into AI Commerce (1).wav" },
    { title: "Unlocking Limitless Possibilities", path: "/assets/Inthelab/Episode 18_ Unlocking Limitless Possibilities—Role Model AI Now Available on the Apple App Store.wav" }
  ];

  const togglePlay = (episodePath: string) => {
    if (currentlyPlaying === episodePath) {
      // Stop current episode
      const audio = audioRefs[episodePath];
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
      setCurrentlyPlaying(null);
    } else {
      // Stop any currently playing episode
      if (currentlyPlaying) {
        const currentAudio = audioRefs[currentlyPlaying];
        if (currentAudio) {
          currentAudio.pause();
          currentAudio.currentTime = 0;
        }
      }

      // Play new episode
      const audio = new Audio(episodePath);
      audio.play();
      setAudioRefs(prev => ({ ...prev, [episodePath]: audio }));
      setCurrentlyPlaying(episodePath);

      // Clean up when audio ends
      audio.onended = () => {
        setCurrentlyPlaying(null);
      };
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Link to="/">
            <motion.button
              className="flex items-center text-gray-400 hover:text-white"
              whileHover={{ x: -5 }}
              transition={{ duration: 0.2 }}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Home
            </motion.button>
          </Link>
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-purple-600 text-transparent bg-clip-text">
            In the Lab: AI-Powered Podcast Series
          </h1>
          
          <div className="mb-8">
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="px-3 py-1 text-sm bg-purple-900/50 text-purple-300 rounded-full">
                AI Podcast
              </span>
              <span className="px-3 py-1 text-sm bg-purple-900/50 text-purple-300 rounded-full">
                Google Notebook LM
              </span>
              <span className="px-3 py-1 text-sm bg-purple-900/50 text-purple-300 rounded-full">
                Consciousness
              </span>
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none mb-12">
            <p className="text-xl text-gray-300 mb-6">
              We've created a groundbreaking podcast series using Google's Notebook LM to explore the intersection of 
              consciousness, technology, and human potential. Each episode is crafted with AI assistance to deliver 
              deep insights and engaging discussions.
            </p>
            
            <h2 className="text-2xl font-bold mt-8 mb-4">The Technology Behind the Podcast</h2>
            <p className="mb-4">
              Using Google's Notebook LM, we've developed a unique approach to podcast creation that combines:
            </p>
            
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>
                <strong>AI-Assisted Research:</strong> Notebook LM helps analyze and synthesize complex topics
                from multiple sources.
              </li>
              <li>
                <strong>Content Structuring:</strong> The AI helps organize information in a way that's both
                engaging and informative.
              </li>
              <li>
                <strong>Natural Language Generation:</strong> Assists in creating scripts that maintain a
                conversational tone while delivering complex information.
              </li>
              <li>
                <strong>Knowledge Integration:</strong> Seamlessly combines information from various sources
                to create comprehensive discussions.
              </li>
            </ul>
            
            <h2 className="text-2xl font-bold mt-8 mb-4">Listen to the Episodes</h2>
            <p className="mb-6">
              Explore our collection of episodes, each focusing on different aspects of consciousness,
              technology, and human potential:
            </p>
          </div>
          
          <div className="space-y-4 mb-12">
            {episodes.map((episode, index) => (
              <div 
                key={episode.path}
                className="bg-gray-900 p-4 rounded-xl border border-white/10 hover:border-purple-500/50 transition-colors duration-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      Episode {index + 1}: {episode.title}
                    </h3>
                  </div>
                  <button
                    onClick={() => togglePlay(episode.path)}
                    className="p-2 rounded-full bg-purple-600 hover:bg-purple-700 transition-colors duration-200"
                  >
                    {currentlyPlaying === episode.path ? (
                      <Pause className="w-5 h-5" />
                    ) : (
                      <Play className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none mb-12">
            <h2 className="text-2xl font-bold mt-8 mb-4">Future Applications</h2>
            <p className="mb-4">
              This case study demonstrates the potential of AI in content creation:
            </p>
            
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>AI-assisted educational content creation</li>
              <li>Automated research and knowledge synthesis</li>
              <li>Personalized learning experiences</li>
              <li>Content creation at scale</li>
            </ul>
            
            <p className="mt-8 text-gray-300">
              Interested in how this technology can enhance your content creation?
              <button 
                onClick={openContactModal}
                className="text-purple-400 hover:text-purple-300 ml-2 underline focus:outline-none"
              >
                Contact us to learn more.
              </button>
            </p>
          </div>
        </motion.div>
      </div>
      
      {/* Contact Form Modal */}
      <Modal 
        isOpen={isContactModalOpen} 
        onClose={closeContactModal}
        title="AI Podcast Creation Inquiry"
      >
        <PodcastContactForm />
      </Modal>
    </div>
  );
} 